import { useState, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { CategoryOption, BreadcrumbItem, ActiveFilter } from './types';
import { useTranslations } from 'next-intl';
import { getLocalizedPath } from '../../i18n/pathnames';
import { getLocalizedCategorySlug } from '../../i18n/categorySlugTranslations';
import { locales, defaultLocale, type Locale } from '../../i18n/config';
import { setCountryCookie, removeCountryCookie } from '../../helpers/countryCookie';

interface UseProductUIProps {
  selectedCategory: string | null;
  selectedCountry: string | null;
  showDiscount: boolean;
  showInStock: boolean;
  searchQuery: string | null;
  categories: CategoryOption[];
  countries: CategoryOption[];
  totalCards: number;
  cardsPerPage: number;
  page: number;
  setPage: (page: number) => void;
  setSelectedCategory: (category: string | null) => void;
  setSelectedCountry: (country: string | null) => void;
  setShowDiscount: (show: boolean) => void;
  setShowInStock: (show: boolean) => void;
  setSearchQuery: (query: string | null) => void;
  updateURL: (category?: string | null, country?: string | null, discount?: boolean, page?: number, search?: string | null, limit?: number, instock?: boolean) => void;
  fetchCards: (params?: Record<string, unknown>) => void;
  filterParams: Record<string, unknown>;
}

export const useProductUI = ({
  selectedCategory,
  selectedCountry,
  showDiscount,
  showInStock,
  searchQuery,
  categories,
  countries,
  totalCards,
  cardsPerPage,
  setPage,
  setSelectedCategory,
  setSelectedCountry,
  setShowDiscount,
  setShowInStock,
  setSearchQuery,
  updateURL,
  fetchCards,
  filterParams
}: UseProductUIProps) => {
  const router = useRouter();
  const t = useTranslations('giftcards');
  const tCommon = useTranslations('common');
  
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  const [countriesExpanded, setCountriesExpanded] = useState(true);
  const [categorySearchFilter, setCategorySearchFilter] = useState('');
  const [countrySearchFilter, setCountrySearchFilter] = useState('');

  // Memoized handlers to prevent re-renders
  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    updateURL(undefined, undefined, undefined, value);
    
    // Calculate updated params with new page value
    const updatedParams = { ...filterParams };
    updatedParams.offset = (value - 1) * (updatedParams.limit as number || 9);
    
    fetchCards(updatedParams);
  }, [setPage, updateURL, fetchCards, filterParams]);

  const handleMobileDrawerToggle = useCallback(() => {
    setMobileDrawerOpen(prev => !prev);
  }, []);

  const handleCategoryClick = useCallback((categoryId: string) => {
    const newCategory = categoryId === selectedCategory ? null : categoryId;
    setSelectedCategory(newCategory);
    setSearchQuery(null);
    setPage(1);
    setMobileDrawerOpen(false);
    updateURL(newCategory, undefined, undefined, 1, null);
  }, [selectedCategory, setSelectedCategory, setSearchQuery, setPage, updateURL]);

  const handleCountryClick = useCallback((countryId: string) => {
    const newCountry = countryId === selectedCountry ? null : countryId;
    if (newCountry) {
      setCountryCookie(newCountry);
    } else {
      removeCountryCookie();
    }
    setSelectedCountry(newCountry);
    setPage(1);
    setMobileDrawerOpen(false);
    updateURL(undefined, newCountry, undefined, 1);
    
    const updatedParams = { ...filterParams };
    updatedParams.offset = 0;
    if (newCountry) {
      updatedParams.country = newCountry;
    } else {
      delete updatedParams.country;
    }
    
    fetchCards(updatedParams);
  }, [selectedCountry, setSelectedCountry, setPage, updateURL, fetchCards, filterParams]);

  const handleDiscountToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newShowDiscount = event.target.checked;
    setShowDiscount(newShowDiscount);
    setPage(1);
    setMobileDrawerOpen(false);
    updateURL(undefined, undefined, newShowDiscount, 1);
    
    const updatedParams = { ...filterParams };
    updatedParams.offset = 0;
    if (newShowDiscount) {
      updatedParams.discount = true;
    } else {
      delete updatedParams.discount;
    }
    
    fetchCards(updatedParams);
  }, [setShowDiscount, setPage, updateURL, fetchCards, filterParams]);

  const handleInStockToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newShowInStock = event.target.checked;
    setShowInStock(newShowInStock);
    setPage(1);
    setMobileDrawerOpen(false);
    updateURL(undefined, undefined, undefined, 1, undefined, undefined, newShowInStock);
    
    const updatedParams = { ...filterParams };
    updatedParams.offset = 0;
    if (newShowInStock) {
      updatedParams.instock = true;
    } else {
      delete updatedParams.instock;
    }
    
    fetchCards(updatedParams);
  }, [setShowInStock, setPage, updateURL, fetchCards, filterParams]);

  // not sure what claude wanted here
  // const handleCardClick = useCallback((brand: string) => {
  //   router.push(`/giftcards/${brand}`);
  // }, [router]);

  const handleCardClick = useCallback((brand: string) => {
    const pathname = window.location.pathname;
    const pathSegments = pathname.split('/').filter(Boolean);
    const currentLocale = (pathSegments[0] && locales.includes(pathSegments[0] as Locale) 
      ? pathSegments[0] 
      : defaultLocale) as Locale;
    
    const localizedGiftcards = getLocalizedPath('/giftcards', currentLocale);
    const brandPath = `${localizedGiftcards}/${brand}`;
    const fullPath = currentLocale === defaultLocale ? brandPath : `/${currentLocale}${brandPath}`;
    router.push(fullPath);
  }, [router]);

  // Memoized clear filter handlers
  const handleClearCategory = useCallback(() => {
    setSelectedCategory(null);
    setPage(1);
    setMobileDrawerOpen(false);
    updateURL(null, undefined, undefined, 1);
  }, [setSelectedCategory, setPage, updateURL]);

  const handleClearCountry = useCallback(() => {
    removeCountryCookie();
    setSelectedCountry(null);
    setPage(1);
    setMobileDrawerOpen(false);
    updateURL(undefined, null, undefined, 1);
  }, [setSelectedCountry, setPage, updateURL]);

  const handleClearDiscount = useCallback(() => {
    setShowDiscount(false);
    setPage(1);
    setMobileDrawerOpen(false);
    updateURL(undefined, undefined, false, 1);
    
    const updatedParams = { ...filterParams };
    updatedParams.offset = 0;
    delete updatedParams.discount;
    
    fetchCards(updatedParams);
  }, [setShowDiscount, setPage, updateURL, fetchCards, filterParams]);

  const handleClearInStock = useCallback(() => {
    setShowInStock(false);
    setPage(1);
    setMobileDrawerOpen(false);
    updateURL(undefined, undefined, undefined, 1, undefined, undefined, false);
    
    const updatedParams = { ...filterParams };
    updatedParams.offset = 0;
    delete updatedParams.instock;
    
    fetchCards(updatedParams);
  }, [setShowInStock, setPage, updateURL, fetchCards, filterParams]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery(null);
    setPage(1);
    setMobileDrawerOpen(false);
    updateURL(undefined, undefined, undefined, 1, null);
    
    // Calculate updated params without search
    const updatedParams = { ...filterParams };
    delete updatedParams.search;
    updatedParams.offset = 0; // Reset to first page
    
    fetchCards(updatedParams);
  }, [setSearchQuery, setPage, updateURL, fetchCards, filterParams]);

  const handleClearAllFilters = useCallback(() => {
    setSelectedCategory(null);
    removeCountryCookie();
    setSelectedCountry(null);
    setShowDiscount(false);
    setShowInStock(false);
    setSearchQuery(null);
    setPage(1);
    setMobileDrawerOpen(false);
    updateURL(null, null, false, 1, null, undefined, false);
    
    // Calculate updated params with all filters cleared
    const updatedParams = {
      limit: filterParams.limit || 9,
      offset: 0
    };
    
    fetchCards(updatedParams);
  }, [setSelectedCategory, setSelectedCountry, setShowDiscount, setShowInStock, setSearchQuery, setPage, updateURL, fetchCards, filterParams]);

  // Memoized computed values to prevent re-renders
  const hasActiveFilters = useMemo(() => 
    Boolean(selectedCategory || selectedCountry || showDiscount || showInStock || searchQuery), 
    [selectedCategory, selectedCountry, showDiscount, showInStock, searchQuery]
  );
  
  const totalPages = useMemo(() => 
    Math.max(1, Math.ceil(totalCards / cardsPerPage)), 
    [totalCards, cardsPerPage]
  );

  const getActiveFilters = useCallback((): ActiveFilter[] => {
    const filters = [];
    
    if (selectedCategory) {
      const category = categories.find(c => c.id === selectedCategory);
      filters.push({
        type: 'category',
        label: `Category: ${category?.name || selectedCategory}`,
        onRemove: handleClearCategory
      });
    }
    
    if (selectedCountry) {
      const country = countries.find(c => c.id === selectedCountry);
      filters.push({
        type: 'country',
        label: `Country: ${country?.name || selectedCountry}`,
        onRemove: handleClearCountry
      });
    }
    
    if (showDiscount) {
      filters.push({
        type: 'discount',
        label: t('activeFilters.discountOnly'),
        onRemove: handleClearDiscount
      });
    }
    
    if (showInStock) {
      filters.push({
        type: 'instock',
        label: t('activeFilters.inStockOnly'),
        onRemove: handleClearInStock
      });
    }
    
    if (searchQuery) {
      filters.push({
        type: 'search',
        label: `Search: "${searchQuery}"`,
        onRemove: handleClearSearch
      });
    }
    
    return filters;
  }, [selectedCategory, selectedCountry, showDiscount, showInStock, searchQuery, categories, countries, handleClearCategory, handleClearCountry, handleClearDiscount, handleClearInStock, handleClearSearch, t]);

  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const pathSegments = pathname.split('/').filter(Boolean);
    const currentLocale = (pathSegments[0] && locales.includes(pathSegments[0] as Locale) 
      ? pathSegments[0] 
      : defaultLocale) as Locale;
    
    const localizedGiftcards = getLocalizedPath('/giftcards', currentLocale);
    const giftcardsPath = currentLocale === defaultLocale ? localizedGiftcards : `/${currentLocale}${localizedGiftcards}`;
    
    const crumbs: BreadcrumbItem[] = [
      { label: tCommon('breadcrumb.home'), path: '/' },
      { label: tCommon('breadcrumb.giftCards'), path: giftcardsPath }
    ];

    if (selectedCategory) {
      const category = categories.find(cat => cat.id === selectedCategory);
      if (category) {
        const localizedCategorySlug = getLocalizedCategorySlug(selectedCategory, currentLocale);
        const categoryPath = currentLocale === defaultLocale 
          ? `${localizedGiftcards}/${localizedCategorySlug}` 
          : `/${currentLocale}${localizedGiftcards}/${localizedCategorySlug}`;
        crumbs.push({
          label: category.name,
          path: categoryPath
        });
      }
    }
    
    return crumbs;
  }, [selectedCategory, categories]);

  const getPageTitle = useCallback(() => {
    if (selectedCategory) {
      const category = categories.find(cat => cat.id === selectedCategory);
      return category ? `${category.name} Gift Cards` : 'Gift Cards';
    }
    return 'All Gift Cards';
  }, [selectedCategory, categories]);

  const filteredCategories = useMemo(() => 
    categories.filter(category => 
      category.name.toLowerCase().includes(categorySearchFilter.toLowerCase())
    ),
    [categories, categorySearchFilter]
  );
  
  const filteredCountries = useMemo(() => 
    countries.filter(country => 
      country.name.toLowerCase().includes(countrySearchFilter.toLowerCase())
    ),
    [countries, countrySearchFilter]
  );

  return {
    // UI State
    mobileDrawerOpen,
    categoriesExpanded,
    countriesExpanded,
    categorySearchFilter,
    countrySearchFilter,
    totalPages,
    breadcrumbs,
    hasActiveFilters,
    filteredCategories,
    filteredCountries,
    
    // UI Handlers
    handlePageChange,
    handleMobileDrawerToggle,
    handleCategoryClick,
    handleCountryClick,
    handleDiscountToggle,
    handleInStockToggle,
    handleCardClick,
    handleClearCategory,
    handleClearCountry,
    handleClearDiscount,
    handleClearInStock,
    handleClearSearch,
    handleClearAllFilters,
    getActiveFilters,
    getPageTitle,
    
    // UI Setters
    setCategoriesExpanded,
    setCountriesExpanded,
    setCategorySearchFilter,
    setCountrySearchFilter
  };
};
